import React, { useMemo } from 'react';
import ReturnShippingAddresses from './ReturnShippingAddresses';
import ReturnShippingRates from './ReturnShippingRates';
import ReturnLabelGeneration from './ReturnLabelGeneration';
import ReturnLabelSuccess from './ReturnLabelSuccess';
import { useReturnLabelGeneration } from '../../../hooks/useReturnLabelGeneration';

const ReturnShippingForm = ({
  // Address props
  selectedCustomerAddress,
  handleCustomerAddressChange,
  selectedMerchantAddress,
  addressesLoading,
  returnDetails = {},
  
  // Shipping rates props
  isLoadingRates,
  ratesError,
  rateData = { rates: [] },
  selectedRate,
  handleRateSelect,
  
  // Generate label props
  requiresPayment,
  fulfillmentGroupId,
  paymentAmount,
  onPaymentSuccess,
  onPaymentError,
  savedPaymentMethods = [],
  isGeneratingLabel,
  orderItemId,
  isReturn = true,
  
  // Existing label data props
  existingLabelData = null,
  allHaveLabels = false
}) => {
  // Use custom hook for label generation logic
  const {
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    isGeneratingReturnLabelApi,
    labelData,
    showLabelSuccess,
    handleGenerateLabel: hookHandleGenerateLabel
  } = useReturnLabelGeneration({
    returnDetails,
    fulfillmentGroupId,
    paymentAmount,
    onPaymentSuccess,
    onPaymentError,
    savedPaymentMethods,
    isReturn
  });

  const isProcessing = isGeneratingLabel || isGeneratingReturnLabelApi;
  const shippingRates = rateData?.rates || [];
  
  // Format shipping rates for display
  const formattedRates = useMemo(() => {
    return shippingRates.map(rate => ({
      id: rate.id || rate.object_id,
      service: rate.serviceName || rate.servicelevel?.name || 'Shipping',
      carrier: rate.provider || rate.carrier_name || rate.carrier || 'Unknown Carrier',
      days: rate.estimated_days || 'N/A',
      price: parseFloat(rate.amount) || 0,
      currency: rate.currency || 'USD'
    }));
  }, [shippingRates]);

  // Flag to show if customer address is selected
  const hasSelectedCustomerAddress = !!selectedCustomerAddress?.id;
  const hasSelectedMerchantAddress = !!selectedMerchantAddress?.id;
  const hasAddressesSelected = hasSelectedCustomerAddress && hasSelectedMerchantAddress;
  
  // Helper to determine if we should show shipping rates or prompts
  const shouldShowRatesSection = hasAddressesSelected;

  // Wrapper function for generate label that passes required params
  const handleGenerateLabel = () => {
    hookHandleGenerateLabel(selectedCustomerAddress, selectedMerchantAddress, selectedRate, requiresPayment);
  };

  // Return existing label UI if labels already exist
  if (allHaveLabels && existingLabelData) {
    return (
      <ReturnLabelSuccess 
        existingLabelData={existingLabelData}
        allHaveLabels={allHaveLabels}
      />
    );
  }

  // Return success UI if new label was just created
  if (showLabelSuccess && labelData) {
    return (
      <ReturnLabelSuccess 
        labelData={labelData}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Shipping Addresses Section */}
      <ReturnShippingAddresses
        selectedCustomerAddress={selectedCustomerAddress}
        handleCustomerAddressChange={handleCustomerAddressChange}
        selectedMerchantAddress={selectedMerchantAddress}
        addressesLoading={addressesLoading}
        returnDetails={returnDetails}
      />

      {/* Shipping Rates Section */}
      <ReturnShippingRates
        shouldShowRatesSection={shouldShowRatesSection}
        ratesError={ratesError}
        isLoadingRates={isLoadingRates}
        formattedRates={formattedRates}
        selectedCustomerAddress={selectedCustomerAddress}
        selectedRate={selectedRate}
        handleRateSelect={handleRateSelect}
      />

      {/* Generate Label Section */}
      <ReturnLabelGeneration
        selectedRate={selectedRate}
        isLoadingRates={isLoadingRates}
        requiresPayment={requiresPayment}
        paymentAmount={paymentAmount}
        savedPaymentMethods={savedPaymentMethods}
        selectedPaymentMethod={selectedPaymentMethod}
        setSelectedPaymentMethod={setSelectedPaymentMethod}
        isProcessing={isProcessing}
        handleGenerateLabel={handleGenerateLabel}
      />
    </div>
  );
};

export default ReturnShippingForm; 